import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { GeoProjectModel, GeoProjectResponse } from '../../../@construction/models/construction-project.model';
import {
  AreaModel,
  TrackerTypeModel,
  TableTypeModel,
  TrackerModel,
  RoadsResponseData,
  TBResponse,
  FenceData,
  UpdatedMVFeederPayload,
  SiteDataType,
  AcBlockWithObservation,
  MvCircuitType,
  ACStationType,
  AcBlockType,
} from '../../../@construction/models/wbs-elements.model';
import { Moment } from 'moment';
import { ELEMENT_TYPE_TO_MAP_LAYER, MapLayerEnum } from '@construction/construction.enums';
import { ProgressRefreshType } from '@construction/modules/project-edit/projects.types';
import { LatestInstallationDateType } from '@construction/construction.types';
import { CSVExportDataType } from '@construction/modules/progress-tracking/progress-tracking.types';
import { DateTimeFormatEnum } from '@core/core.enum';
import { ConstructionProjectSignalStore } from '@construction/stores/construction-project/construction-project-signal-store.service';
import { LayerConfigurationService } from '@construction/services/layer-configuration.service';

export interface IUpdateStatus {
  status?: string;
  built_at?: string | null;
  draft_built_at?: string | null;
  epc_built_at?: Date | null;
  perspective?: string;
  drone_ortho_id?: string;

  elements: {
    [key: string]: string[];
  };
  epc_installed_by?: string | null;
}

export type DTElementsDuplicateCountsType = {
  duplicate_name_cnt: number;
  duplicate_geom_cnt: number;
  sum_cnt: number;
};

export type DuplicatesDataTableType = {
  element: MapLayerEnum;
  duplicate_name_cnt: number;
  duplicate_geom_cnt: number;
  sum_cnt: number;
};

export type TrackerProductionTracking = {
  epc_built_at: string;
  epc_completed_modules_at: string;
  epc_installed_by: string;
  id: string;
};

export type ElementProgressData = {
  total: number;
  actual: number;
  relative_plan: number;
};

export type DateCountData = {
  date: string;
  count: number;
};

export type WBSElementReportData = {
  element: string;
  total_count: number;
  actual_count: DateCountData[];
  planned_count: DateCountData[];
};

/** Return shape of `ConstructionGeoApiService`'s private `calculateValues`, exposed so consumers of `getDashboardProgress` can type its `calculatedData` without duplicating the shape. */
export type CalculatedProgressItem = {
  wbsElement: string;
  dictionaryKey: string;
  fallbackLabel?: string;
  actual: number;
  plan: number;
  delta: number;
  weight: number;
};

type FlagElements = {
  flagged_at: string | Date | null;
  elements: {
    modules: Array<string>;
  };
};

export type Holiday = {
  name: string;
  date: string;
  country: string;
};

export type PlanDates = {
  plan_start: string;
  plan_end: string;
};

/** DT2 response shape: plan dates keyed by plural element name (e.g. `piers`). */
export type ElementsPlanDatesWbsConfigDt2 = {
  trackers: PlanDates;
  feeders: PlanDates;
  combiner_boxes: PlanDates;
  pcs: PlanDates;
  piers: PlanDates;
  tables: PlanDates;
  modules: PlanDates;
  dc_harnesses: PlanDates;
  dc_feeders: PlanDates;
  cbx: PlanDates;
  mv_feeders: PlanDates;
  ac_stations: PlanDates;
  holes: PlanDates;
  bess: PlanDates;
  substations: PlanDates;
  roads: PlanDates;
  fences: PlanDates;
};

/** DT3 response entry: `node_type_name` is the lower-case, singular node type (e.g. `pier`). */
export type ElementPlanDatesDt3 = PlanDates & {
  node_type_uuid: string;
  node_type_name: string;
};

export type ElementsPlanDatesWbsConfig = ElementsPlanDatesWbsConfigDt2 | ElementPlanDatesDt3[];

export type MvCircuitsBoundingBox = {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
};

export { FlagElements };

@Injectable({
  providedIn: 'root',
})
export class ConstructionGeoApiService {
  private http = inject(HttpClient);
  private projectStore = inject(ConstructionProjectSignalStore);
  private layerConfigurationService = inject(LayerConfigurationService);
  /** Static config, safe to fetch once for this singleton service instead of on every progress fetch. */
  private readonly layerConfigs = this.layerConfigurationService.getAllLayerConfigs();

  /**
   * Path segment for an element-detail endpoint of the currently loaded project. DT3 projects
   * serve element data under the singular node type (`.../tracker/{id}/`), DT2 under the plural
   * layer (`.../trackers/{id}/`).
   */

  public getProjectData(projectUUID?: string): Observable<GeoProjectModel> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/`;

    return this.http.get<TBResponse<GeoProjectModel>>(url).pipe(map((res) => res.data));
  }

  public getChildWBSEElements(
    wbse?: string,
    parent?: string | Array<string>,
    id?: string,
    page = 1,
    projectUUID?: string,
    pageSize = 15,
  ): Observable<GeoProjectResponse> {
    let qry = {};
    if (typeof parent === 'string') {
      switch (parent) {
        case 'area':
          qry = { ...qry, area_id: id };
          break;
        case 'tracker':
          qry = { ...qry, tracker_id: id };
          break;
        case 'feeder':
          qry = { ...qry, feeder_id: id, page: page };
          break;
        case 'bha':
          qry = { ...qry, bha_id: id };
          break;
        case 'damper':
          qry = { ...qry, damper_id: id };
          break;
        case 'post':
          qry = { ...qry, post_id: id };
          break;
        case 'project':
          qry = { ...qry, project_id: id };
          break;
        case 'slew':
          qry = { ...qry, slew_id: id };
          break;
        case 'torque_tube_id':
          qry = { ...qry, torque_tube_id: id };
          break;
        case 'combiner_boxes':
          qry = { ...qry, combiner_box_id: id };
          break;
        case 'combiner_box':
          qry = { ...qry, combiner_box_id: id };
          break;
        case 'dc_feeder':
          qry = { ...qry, dc_feeder_id: id };
          break;
        case 'pcs':
          qry = { ...qry, pcs_id: id };
          break;
        case 'ac_station':
          qry = { ...qry, ac_station_id: id };
          break;
        case 'mv_feeder':
          qry = { ...qry, mv_feeder_id: id };
          break;
        case 'cbx':
          qry = { ...qry, cbx_id: id };
          break;
        case 'type':
          qry = { ...qry, type_id: id };
          break;
        default:
          qry = { ...qry };
      }
    } else if (parent && typeof parent !== 'string' && parent?.length > 1) {
      parent.map(() => {
        qry = { ...qry, ...{ value_key: id, page: page } };
      });
    }

    const params = new HttpParams({ fromObject: { ...qry, page: page, page_size: pageSize } });
    const elementType = wbse ? this.elementPath(wbse) : wbse;
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${elementType}/`;

    return this.http.get<GeoProjectResponse>(url, { params });
  }

  public getSite(projectUUID: string): Observable<AreaModel[]> {
    return this.getGroupsByType(projectUUID, MapLayerEnum.SITE);
  }

  public getAreas(projectUUID: string): Observable<AreaModel[]> {
    return this.getGroupsByType(projectUUID, MapLayerEnum.AREA);
  }

  public getMvCircuits(projectUUID: string): Observable<AreaModel[]> {
    return this.getGroupsByType(projectUUID, MapLayerEnum.FEEDERS);
  }

  public getDetailedMvCircuits(projectUUID: string, pageSize = 100): Observable<MvCircuitType[]> {
    const elementType = this.elementPath(MapLayerEnum.FEEDERS);
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${elementType}/`;
    let params = new HttpParams();
    params = params.append('page_size', pageSize);

    return this.http.get<TBResponse<MvCircuitType[]>>(url, { params }).pipe(map((res) => res.data));
  }

  public getDetailedAcStations(projectUUID: string, pageSize = 100): Observable<ACStationType[]> {
    const elementType = this.elementPath(MapLayerEnum.AC_STATIONS);
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${elementType}/`;
    let params = new HttpParams();
    params = params.append('page_size', pageSize);

    return this.http.get<TBResponse<ACStationType[]>>(url, { params }).pipe(map((res) => res.data));
  }

  public patchFeeder(projectUUID: string, data: UpdatedMVFeederPayload): Observable<void> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/ccc/feeders/`;

    return this.http.patch<void>(url, data);
  }

  public getPCSs(projectUUID: string | undefined): Observable<AreaModel[]> {
    return this.getGroupsByType(projectUUID, MapLayerEnum.PCS);
  }

  // DT3-only grouping (see SlicerGroupsComponent for the equivalent slicer-side fetch) — DT2
  // projects have no substation hierarchy, so callers must gate on isDt3Enabled before using this.
  public getSubstationGroups(projectUUID: string | undefined): Observable<AreaModel[]> {
    return this.getGroupsByType(projectUUID, MapLayerEnum.SUBSTATION_GROUP);
  }

  /**
   * Lists group nodes through the `/all/{group_type}/` route, using the vocabulary selected by the
   * project's DT3 flag. This route is distinct from the generic element list/detail route.
   */
  public getGroupsByType(projectUUID: string | undefined, layer: MapLayerEnum): Observable<AreaModel[]> {
    const groupType = this.elementPath(layer);
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/all/${groupType}/`;

    return this.http.get<TBResponse<AreaModel[]>>(url).pipe(map((response) => response.data));
  }

  public getElementsByType(projectUUID: string, layer: MapLayerEnum): Observable<AreaModel[]> {
    const elementType = this.elementPath(layer);
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${elementType}/`;

    return this.http.get<TBResponse<AreaModel[]>>(url).pipe(map((res) => res.data));
  }

  public getDetaildAcBlocks(projectUUID: string | undefined): Observable<AcBlockType[]> {
    const elementType = this.elementPath(MapLayerEnum.PCS);
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${elementType}/`;

    return this.http.get<TBResponse<AcBlockType[]>>(url).pipe(map((res) => res.data));
  }

  public getTrackerTypes(projectUUID: string): Observable<TrackerTypeModel[]> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/tracker_type/`;

    return this.http.get<TBResponse<TrackerTypeModel[]>>(url).pipe(map((res) => res.data));
  }

  public getTableTypes(projectUUID: string): Observable<TableTypeModel[]> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/table_type/`;

    return this.http.get<TBResponse<TableTypeModel[]>>(url).pipe(map((res) => res.data));
  }

  public getAreaData(projectUUID: string, areaId: string): Observable<TrackerModel> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.AREA)}/${areaId}/`;

    return this.http.get<TBResponse<TrackerModel>>(url).pipe(map((res) => res.data));
  }

  public getModuleTypes(projectUUID: string): Observable<TableTypeModel[]> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/module_types/`;

    return this.http.get<TBResponse<TableTypeModel[]>>(url).pipe(map((res) => this.transformDates(res.data)));
  }

  public getTrackerData(projectUUID: string, trackerId: string): Observable<TrackerModel> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.TRACKERS)}/${trackerId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.anchor) {
          res.data.anchor.coordinates = this.getCoords(res.data.anchor.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getACStationsData(projectUUID: string, acStationsId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.AC_STATIONS)}/${acStationsId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getPCSData(projectUUID: string, pcsId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.PCS)}/${pcsId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getMVCircuitData(projectUUID: string, feederId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.FEEDERS)}/${feederId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getMVFeedersData(projectUUID: string, mvFeedersId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.MV_FEEDERS)}/${mvFeedersId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getDCFeedersData(projectUUID: string, dcFeedersId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.DC_FEEDERS)}/${dcFeedersId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getDCHarnessesData(projectUUID: string, dcHarnessesId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.DC_HARNESSES)}/${dcHarnessesId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getCombinerBoxes(projectUUID: string, combinerBoxesId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.COMBINER_BOXES)}/${combinerBoxesId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getCBX(projectUUID: string, cbxId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.CBX)}/${cbxId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getPiersData(projectUUID: string, pierId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.PIERS)}/${pierId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getHolesData(projectUUID: string, holeId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.HOLES)}/${holeId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getTablesData(projectUUID: string, tableId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.TABLES)}/${tableId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public getModulesData(projectUUID: string, moduleId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/${this.elementPath(MapLayerEnum.MODULES)}/${moduleId}/`;

    return this.http.get(url).pipe(
      map((res: any) => {
        if (res.data.center) {
          res.data.center.coordinates = this.getCoords(res.data.center.coordinates);
        }

        return this.transformDates(res.data);
      }),
    );
  }

  public updateStatus(data: IUpdateStatus, projectUUID: string, perspective: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/wbselements/status/`;
    const params = new HttpParams({ fromObject: { perspective } });

    return this.http.patch(url, data, { params });
  }

  public updateDraftStatus(data: IUpdateStatus, projectUUID: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/wbselements/status_draft/`;

    return this.http.patch(url, data);
  }

  public updateEpcTrackerStatusAndSubelements(data: IUpdateStatus, projectUUID: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/wbselements/install_epc_tracker_and_subelements/`;

    return this.http.patch(url, data);
  }

  public getDashboardProgress(
    projectId: string,
    weights: Record<string, number>,
    timezone: string,
    refresh = false,
    fromDate = '',
    toDate = '',
    isDt3Enabled = false,
  ) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/construction_projects/${projectId}/dashboard_progress/`;

    return this.http
      .get<
        TBResponse<ProgressRefreshType>
      >(url, { params: { refresh, from_date: fromDate, to_date: toDate, timezone } })
      .pipe(
        map((res) => {
          const data = res.data;

          const calculatedData = this.calculateProgressValues(weights, data, isDt3Enabled);

          return { lastRefreshedDate: data.last_refreshed, calculatedData: calculatedData };
        }),
      );
  }

  public getReportData(params: any): Observable<WBSElementReportData> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/construction_projects/${params.id}/element_progress`;

    return this.http.get(url, { params }).pipe(map((res: any) => ({ ...res.data, element: params.element })));
  }

  public deleteHoles(geoProjectId: string, ids: string[]): Observable<any> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/holes/delete/`;

    return this.http.post(url, { hole_ids: ids });
  }

  public createHolesForSpecificPiers(
    projectID: string,
    data: { is_installed: boolean; pier_ids: Array<string> },
    perspective: string,
  ) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectID}/hole_for_pier/?perspective=${perspective}`;

    return this.http.post(url, data).pipe(map((res: any) => res.data));
  }

  public getPreviousWorkDayOfConstructionProject(projectUUID: string, date: Date | string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/workdays/previous/?beginning_date=${date}`;

    return this.http.get(url).pipe(map((res: any) => res.data));
  }

  public validateIfHoleExistsOnPier(projectUUID: string, pierIds: string[]) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${projectUUID}/piers/validate_existing_holes/`;

    return this.http.post(url, { pier_ids: pierIds }).pipe(map((res: any) => res.data));
  }

  public validateElementInstallation(
    geoProjectId: string,
    date: string | null,
    elements: any,
    perspective: string,
  ): Observable<any> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/wbselements/validate/?perspective=${perspective}`;

    return this.http.post(url, { date: date, elements: { ...elements } });
  }

  public flagWBSElement(geoProjectId: string, data: FlagElements): Observable<unknown> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/wbselements/flag/status/`;

    return this.http.patch(url, data);
  }

  public getBessData(geoProjectId: string, bessId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/${this.elementPath(MapLayerEnum.BESS)}/${bessId}/`;

    return this.http.get(url).pipe(map((res: any) => res.data));
  }

  public getSubstationData(geoProjectId: string, substationId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/${this.elementPath(MapLayerEnum.SUBSTATIONS)}/${substationId}/`;

    return this.http.get(url).pipe(map((res: any) => res.data));
  }

  /**
   * DT3 substation sub-elements (MPT, control building, ground grid, breakers, ...) all share the
   * same element-detail shape and are served under their singular node-type segment (e.g.
   * `.../mpt/{id}/`). `elementPath` resolves that DT3 segment, so one accessor covers every
   * substation sub-element layer instead of a near-identical method per type.
   */
  public getSubstationSubElementData(geoProjectId: string, layer: MapLayerEnum, elementId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/${this.elementPath(layer)}/${elementId}/`;

    return this.http.get(url).pipe(map((res: any) => res.data));
  }

  public getRoadsData(geoProjectId: string, roadId: string): Observable<RoadsResponseData> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/${this.elementPath(MapLayerEnum.ROADS)}/${roadId}/`;

    return this.http.get<TBResponse<RoadsResponseData>>(url).pipe(map((res) => res.data));
  }

  public getFencesData(geoProjectId: string, fenceId: string): Observable<FenceData> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/${this.elementPath(MapLayerEnum.FENCES)}/${fenceId}/`;

    return this.http.get<TBResponse<FenceData>>(url).pipe(map((res) => res.data));
  }

  public getSiteData(id: string) {
    // DT3 serves the site (project) detail under the singular `project` node-type segment, like every
    // other element (see elementPath). DT2 keeps the legacy `advanced` endpoint.
    const segment = this.projectStore.isDt3Enabled() ? this.elementPath(MapLayerEnum.SITE) : 'advanced';
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${id}/${segment}/`;

    return this.http.get<TBResponse<SiteDataType>>(url).pipe(map((res) => res.data));
  }

  public getLatestInstallationDate(id: string, perspective = 'owner'): Observable<LatestInstallationDateType> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/construction_projects/${id}/get_last_installation_dates/?perspective=${perspective}`;

    return this.http.get<TBResponse<LatestInstallationDateType>>(url).pipe(map((res) => res.data));
  }

  public getAcBlocksWithObservations(geoProjectId: string, mvCirciuts?: string): Observable<AcBlockWithObservation[]> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${geoProjectId}/qc_issues/pcs/`;

    return this.http
      .post<TBResponse<AcBlockWithObservation[]>>(url, { with_issues: false, feeder_ids: mvCirciuts })
      .pipe(map((res) => res.data));
  }

  public sendCSVExportToMail(projectId: string, data: CSVExportDataType[]) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/construction_projects/${projectId}/send_csv_progress/`;

    return this.http.post<CSVExportDataType>(url, { reports: data });
  }

  public getDailyQuotasForSensors(geoProjectUUID: string, from: Moment, to: Moment) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectUUID}/daily_quotas/`;

    return this.http
      .get<TBResponse<{ sensor_name: string; quota: number; date: string }[]>>(url, {
        params: {
          from_date: from.format(DateTimeFormatEnum.StandardDateFormat),
          to_date: to.format(DateTimeFormatEnum.StandardDateFormat),
        },
      })
      .pipe(map((response) => response.data));
  }

  public getTrackersForProductionTracking(geoProjectUUID: string, from: Moment, to: Moment) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectUUID}/trackers/production_tracking/`;

    return this.http
      .get<TBResponse<TrackerProductionTracking[]>>(url, {
        params: {
          epc_completed_modules_at_from_date: from.utc().format(),
          epc_completed_modules_at_to_date: to.utc().format(),
        },
      })
      .pipe(
        map((response) =>
          response.data.reduce((acc, tracker) => {
            const key = tracker.epc_installed_by;
            if (!acc.has(key)) {
              acc.set(key, []);
            }
            acc.get(key)!.push(tracker);

            return acc;
          }, new Map<string, typeof response.data>()),
        ),
      );
  }

  public getDTElementsDuplicateCount(
    geoProjectUUID: string,
    element: MapLayerEnum,
  ): Observable<DuplicatesDataTableType> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectUUID}/elements_duplicate_cnt/?element=${element}`;

    return this.http
      .get<TBResponse<DTElementsDuplicateCountsType>>(url)
      .pipe(map((response) => ({ ...response.data, element })));
  }

  public getElementsPlanDatesWbsConfig(constructionProjectId: string): Observable<ElementsPlanDatesWbsConfig> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/construction_projects/${constructionProjectId}/get_start_and_end_plan_dates/`;

    return this.http.get<TBResponse<ElementsPlanDatesWbsConfig>>(url).pipe(map((response) => response.data));
  }

  public getBoudingBoxForMvCircuits(geoProjectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/geo_service/project/${geoProjectId}/feeders/bounding_box/`;

    return this.http.get<TBResponse<MvCircuitsBoundingBox>>(url).pipe(map((response) => response.data));
  }

  private getCoords(pointArr: Array<number>, decimals = 8): Array<string> | undefined {
    return pointArr ? pointArr.map((x) => Number(x).toFixed(decimals)) : undefined;
  }

  /**
   * On a DT3 project, the settings UI saves every element's weight (and `dictionary_of_labels`
   * entry) under its DT3 node-type key (singular: `ac_station`, `pier`, `hole`, ...), but the
   * `dashboard_progress/` response still keys most elements by their legacy plural field name
   * (`ac_stations`, `piers`, `holes`, ...) — only the substation sub-elements from PRD-817 already
   * match (they're singular in both places). Resolve the response key back to its `MapLayerEnum`
   * (identity for a legacy plural key, `ELEMENT_TYPE_TO_MAP_LAYER` for an already-singular DT3 key)
   * so the two naming schemes can be lined back up.
   */
  private resolveLayer(responseKey: string): MapLayerEnum | undefined {
    return (Object.values(MapLayerEnum) as string[]).includes(responseKey)
      ? (responseKey as MapLayerEnum)
      : ELEMENT_TYPE_TO_MAP_LAYER[responseKey];
  }

  private resolveWeightKey(layer: MapLayerEnum | undefined, responseKey: string, isDt3Enabled: boolean): string {
    return layer ? this.layerConfigurationService.getWeightKey(layer, isDt3Enabled) : responseKey;
  }

  /** Same translation as `resolveWeightKey`, but for the `dictionary_of_labels` key. */
  private resolveDictionaryKey(layer: MapLayerEnum | undefined, responseKey: string, isDt3Enabled: boolean): string {
    return layer ? this.layerConfigurationService.getDictionaryKey(layer, isDt3Enabled) : responseKey;
  }

  /**
   * A project's `dictionary_of_labels` entry can exist but be blank (DT3's `list_elements`
   * always returns the full {singular, plural, abbreviation} shape, coalescing an unconfigured
   * element to ''). When that happens, fall back to the static default label from
   * `digital-twin-structure.json` — the same source `LabelDictionaryService` falls back to for
   * the full project view — instead of showing the raw backend key. Reads from `this.layerConfigs`
   * (fetched once for this singleton service, not once per project/element).
   */
  private resolveFallbackLabel(layer: MapLayerEnum | undefined): string | undefined {
    return layer ? this.layerConfigs.find((config) => config.key === layer)?.labels?.plural || undefined : undefined;
  }

  /**
   * DT2 kept a bespoke plan formula for substations (`relative_plan` was already a 0-1 fraction,
   * not a count to compare against `total`). DT3 node types (including the substation sub-elements
   * from PRD-817) all use the same `calculateAbsPct` formula as every other element, so the special
   * case only applies pre-DT3.
   */
  private calculateValues(
    weights: Record<string, number>,
    wbseName: string,
    data: any,
    isDt3Enabled: boolean,
  ): CalculatedProgressItem {
    const item = data[wbseName];
    const layer = this.resolveLayer(wbseName);
    const weightKey = this.resolveWeightKey(layer, wbseName, isDt3Enabled);
    const dictionaryKey = this.resolveDictionaryKey(layer, wbseName, isDt3Enabled);
    const fallbackLabel = this.resolveFallbackLabel(layer);

    const weight = weights && weightKey in weights ? weights[weightKey] : 0;
    const actual = this.calculateAbsPct(item.actual, item.total) * 100;
    const plan =
      wbseName === MapLayerEnum.SUBSTATIONS && !isDt3Enabled
        ? item.relative_plan * 100 || 0
        : this.calculateAbsPct(item.relative_plan, item.total) * 100;

    return {
      wbsElement: wbseName,
      dictionaryKey,
      fallbackLabel,
      actual: actual,
      plan: plan,
      delta: actual - plan,
      weight: weight,
    };
  }

  private calculateAbsPct(x: number, tot: number) {
    return tot ? (x || 0) / tot : 0;
  }

  /**
   * The backend already returns exactly the element/node-type keys that apply to a project (DT2's
   * flat set, or DT3's set including the substation sub-elements from PRD-817/830) — so this just
   * walks whatever keys are actually in the response instead of a hardcoded element list.
   * `isDt3Enabled` is only needed by `calculateValues` to pick the right plan formula. Keys are also
   * filtered by shape (not just by excluding `last_refreshed`), so a future non-element metadata
   * field on the response doesn't crash `calculateValues`'s `item.actual`/`item.total` access.
   */
  private calculateProgressValues(
    weights: Record<string, number>,
    data: ProgressRefreshType,
    isDt3Enabled = false,
  ): CalculatedProgressItem[] {
    return Object.keys(data)
      .filter((key) => {
        const item = (data as Record<string, unknown>)[key];

        return key !== 'last_refreshed' && !!item && typeof item === 'object' && 'total' in item;
      })
      .map((key) => this.calculateValues(weights, key, data, isDt3Enabled));
  }

  private transformDates(data: any) {
    if (Array.isArray(data)) {
      return data.map((item) => this.mapDates(item));
    }

    return this.mapDates(data);
  }

  private mapDates(item: any) {
    if (!item) return item;

    return {
      ...item,
      built_at: item.built_at ? new Date(item.built_at) : undefined,
      plan_end: item.plan_end ? new Date(item.plan_end) : undefined,
      plan_start: item.plan_start ? new Date(item.plan_start) : undefined,
    };
  }

  private elementPath(layer: MapLayerEnum | string): string {
    return this.layerConfigurationService.getElementEndpointSegment(layer, this.projectStore.isDt3Enabled());
  }
}
